/**
 * ChannelContext.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경] B2B(거래처)와 B2C(개인고객) 두 채널을 단일 코드베이스에서 운영하기 위한 Context
 * 
 * - b2b: 기존 거래처 전용 서비스 (매장 코드 입력 O)
 * - b2c: 개인 고객 전용 서비스 (매장 코드 입력 X, 개인 특화 UI)
 * 
 * [사용법]
 * - routes.tsx에서 ChannelLayout으로 라우트 그룹을 감싸면 자동 적용
 * - 하위 컴포넌트에서 useChannel(), useChannelPath() 훅으로 접근
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { createContext, useContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

/** 채널 타입: b2b=거래처, b2c=개인고객 */
export type ChannelType = 'b2b' | 'b2c';

interface ChannelContextValue {
  channel: ChannelType;
  /** 채널이 개인고객(B2C)인지 여부 */
  isPersonal: boolean;
  /** 채널 기반 경로 접두사 (b2b='', b2c='/dawontrade-in') */
  pathPrefix: string;
}

const ChannelContext = createContext<ChannelContextValue>({
  channel: 'b2b',
  isPersonal: false,
  pathPrefix: '',
});

/** 현재 채널 정보를 반환하는 훅 */
export const useChannel = (): ChannelContextValue => useContext(ChannelContext);

/**
 * 채널에 따라 경로를 자동 변환하는 훅
 * @example
 * const channelPath = useChannelPath();
 * channelPath('/apply') // b2b → '/apply', b2c → '/dawontrade-in/apply'
 * channelPath('/')      // b2b → '/',      b2c → '/dawontrade-in'
 */
export const useChannelPath = () => {
  const { pathPrefix } = useChannel();
  return (path: string): string => {
    if (path === '/') return pathPrefix || '/';
    return `${pathPrefix}${path}`;
  };
};

interface ChannelLayoutProps {
  channel: ChannelType;
}

/**
 * 채널별 Layout Route 컴포넌트
 * routes.tsx에서 <Route element={<ChannelLayout channel="b2c" />}> 형태로 사용
 */
export const ChannelLayout = ({ channel }: ChannelLayoutProps) => {
  const value = useMemo<ChannelContextValue>(() => ({
    channel,
    isPersonal: channel === 'b2c',
    pathPrefix: channel === 'b2c' ? '/dawontrade-in' : '',
  }), [channel]);

  return (
    <ChannelContext.Provider value={value}>
      <Outlet />
    </ChannelContext.Provider>
  );
};

export default ChannelContext;
